import { isPlatformBrowser } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, NgZone, OnDestroy, OnInit, PLATFORM_ID } from "@angular/core";
import { TranslateService } from "@shared-services/translate.service";
import { ServiceSection } from "@core-models/base-service-section.model";
import { PageHelper } from "@core-constants/page-helper.const";

@Component({
  selector: "app-showcase-carousel",
  templateUrl: "./showcase-carousel.component.html",
  styleUrls: ["./showcase-carousel.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShowCaseCarouselComponent implements OnInit, OnDestroy
{
  public time: number = 5000;

  @Input() public  items: ServiceSection.ICarouselItem[] = [];
  @Input() public set timeSeconds(value: number)
  {
    if (value > 0)
    {
       this.time = value * 1000;
    }
  }

  public currentIndex = 0;
  public autoplayInterval: any;

  constructor(@Inject(PLATFORM_ID) private platformId,
    protected translateService: TranslateService,
    private ngZone: NgZone,
    private changeDetector: ChangeDetectorRef) { }


  public ngOnInit(): void
  {
    this.startAutoplay();
  }

  public ngOnDestroy(): void
  {
    this.stopAutoplay();
  }

  public startAutoplay(): void
  {
    if (isPlatformBrowser(this.platformId))
    {
      this.ngZone.runOutsideAngular(() =>
      {
        this.autoplayInterval = setInterval(() =>
        {
          this.ngZone.run(() =>
          {
            this.next();
          });
        }, this.time);
      });
    }
  }

  public stopAutoplay(): void
  {
    if (isPlatformBrowser(this.platformId))
    {
      if (this.autoplayInterval)
      {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }
    }
  }

  public prev(): void
  {
    this.currentIndex = (this.currentIndex - 1 + this.items.length) % this.items.length;
    this.changeDetector.markForCheck();
    this.resetAutoplay();
  }

  public next(): void
  {
    this.currentIndex = (this.currentIndex + 1) % this.items.length;
    this.changeDetector.markForCheck();
    this.resetAutoplay();
  }

  public goToSlide(index: number): void
  {
    this.currentIndex = index;
    this.changeDetector.markForCheck();
    this.resetAutoplay();
  }

  private resetAutoplay(): void
  {
    this.stopAutoplay();
    this.startAutoplay();
  }

  /** Devuelve los 3 elementos visibles (prev, current, next) */
  public getVisibleItems(): ServiceSection.IVisibleCarouselItem[]
  {
    const n = this.items.length;
    if (n === 0)
    {
      return [];
    }

    const prevIdx = (this.currentIndex - 1 + n) % n;
    const nextIdx = (this.currentIndex + 1) % n;

    if (this.isMobile)
    {
      return [
        { ...this.items[this.currentIndex], position: 'center' }
      ];
    }

    return [
      { ...this.items[prevIdx], position: 'left' },
      { ...this.items[this.currentIndex], position: 'center' },
      { ...this.items[nextIdx], position: 'right' }
    ];
  }

  /** Aplica clases según la posición */
  public getCardClassesByPosition(position: string): string
  {
    switch (position)
    {
      case 'center':
        return "carousel-card-lg";
      case 'left':
      case 'right':
        return "carousel-card-sm d-none d-md-block";
      default:
        return "d-none";
    }
  }

  public trackByFn(index: number, item: ServiceSection.ICarouselItem): number
  {
    return index;
  }

  public get isMobile(): boolean
  {
    let isMob: boolean = false;

    if (isPlatformBrowser(this.platformId))
    {
      isMob = window.innerWidth <= PageHelper.MobileWidth;
    }

    return isMob;
  }
}
